import * as React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, GraduationCap } from "lucide-react";
import { Button, Card, CardContent } from "@eduviaos/ui-kit";
import type { TenantVerticalLiteral } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useFirstRun } from "@/hooks/useFirstRun";
import { FirstRunShell } from "@/components/FirstRunShell";
import { buildFirstRunSteps, firstRunEyebrow } from "@/components/firstRunSteps";

const VERTICAL_LABELS: Record<string, string> = {
  school: "School",
  coaching_institute: "Coaching institute",
  study_centre: "Study centre",
};

/**
 * Phase C1a — IA tenant first-run welcome step.
 *
 * The principal lands here on first login (or right after redeeming the
 * onboarding-accept invite). Renders a read-only summary of what the
 * Super-Admin pre-configured for the tenant + a single primary CTA that
 * flips ``tenant.first_run_complete`` and drops the user on the
 * dashboard.
 *
 * Phase C1c added the import-students step, so the wizard is now
 * welcome → invite-staff → import-students → dashboard.
 */
export function FirstRunWelcomePage() {
  const navigate = useNavigate();
  const { firstRunComplete, status } = useAuth();
  const stateQuery = useFirstRun();

  // Anyone hitting this URL with first_run_complete already true is
  // bounced straight to /dashboard. Keeps the wizard from being a
  // permanent re-entry hatch and matches Phase C's spec
  // ("routing guard sends Owner / Principal here only when False").
  if (status === "authed" && firstRunComplete === true) {
    return <Navigate to="/dashboard" replace />;
  }

  // SC Foundation (F1) — de-school the wizard for study centres: the
  // step rail + the "invite" hand-off copy read "team", not "teachers
  // and staff". School / coaching are unchanged.
  // BE types `vertical` as a plain string; narrow to the FE literal
  // union (the value is always one of the three configured verticals).
  const vertical = (stateQuery.data?.vertical ?? "school") as TenantVerticalLiteral;
  const isStudyCentre = vertical === "study_centre";
  const steps = buildFirstRunSteps(vertical);

  const onContinue = () => {
    // Phase F2b — welcome now hands off to the branding step (was
    // invite-staff). The wizard's other "exit" path is the "Skip &
    // finish setup" pill in the shell, which still calls
    // /me/first-run/complete directly.
    navigate("/first-run/branding");
  };

  return (
    <FirstRunShell
      steps={steps}
      activeStep="welcome"
      eyebrow={firstRunEyebrow(vertical, "welcome")}
      showSkipExit={false}
    >
      <header className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 grid gap-5 text-center motion-safe:duration-500">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
          {stateQuery.data ? (
            <>
              Welcome to <span className="text-primary">{stateQuery.data.tenant_name}</span>
            </>
          ) : (
            "Welcome aboard"
          )}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-xl text-balance text-sm leading-relaxed sm:text-base">
          Your EduViaOS workspace has been pre-configured by your account manager. Review what's set
          up below, then continue to add your logo and colours and invite your{" "}
          {isStudyCentre ? "team" : "first teachers and staff"}.
        </p>
      </header>

      <Card className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 mt-10 motion-safe:duration-700">
        <CardContent className="grid gap-6 p-6 sm:grid-cols-2 sm:gap-8 sm:p-8">
          <SummaryItem
            icon={<GraduationCap className="size-5" aria-hidden="true" />}
            label="Institution type"
            value={
              stateQuery.data
                ? (VERTICAL_LABELS[stateQuery.data.vertical] ?? stateQuery.data.vertical)
                : "—"
            }
          />
          <SummaryItem
            icon={<CheckCircle2 className="size-5" aria-hidden="true" />}
            label="Workspace"
            value={stateQuery.data ? `${stateQuery.data.tenant_slug}.eduviaos.app` : "—"}
            mono
          />
        </CardContent>
      </Card>

      <div className="motion-safe:animate-in motion-safe:fade-in mt-10 grid gap-3 motion-safe:delay-150 motion-safe:duration-700">
        <Button
          size="lg"
          onClick={onContinue}
          disabled={stateQuery.isLoading}
          className="group mx-auto w-full max-w-sm gap-2 transition-transform duration-150 ease-out active:scale-[0.97]"
        >
          Continue
          <ArrowRight
            className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Button>
        <p className="text-muted-foreground mx-auto max-w-sm text-center text-xs leading-relaxed">
          You can fine-tune modules, branding, and admissions later from Settings.
        </p>
      </div>
    </FirstRunShell>
  );
}

interface SummaryItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}

function SummaryItem({ icon, label, value, mono }: SummaryItemProps) {
  return (
    <div className="flex items-start gap-3">
      <span className="bg-primary/10 text-primary grid size-10 shrink-0 place-items-center rounded-md">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wider">
          {label}
        </p>
        <p className={`mt-1 text-sm font-semibold ${mono ? "font-mono text-[13px]" : ""}`}>
          {value}
        </p>
      </div>
    </div>
  );
}
